import React, { useState, useEffect } from "react";
import { Add_BONUSE } from "../../hooks/APIHooks";
import { FaPlusCircle, FaUser, FaMoneyBillWave, FaHistory, FaCalendarAlt, FaIdBadge, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import LogoLoader from "../../components/LogoLoader/LogoLoader";

const AddBonus = () => {
  const [userId, setUserId] = useState("");
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch bonuses (or after adding a bonus)
  const fetchBonuses = async (postData = null) => {
    if (!postData) setLoading(true);
    try {
      const response = await fetch(Add_BONUSE, {
        method: postData ? "POST" : "GET",
        headers: {
          "Content-Type": "application/json",
        },
        body: postData ? JSON.stringify(postData) : null,
      });

      const data = await response.json();
      if (data.success) {
        setBonuses(data.bonuses || []);
        if (data.message) setMessage(data.message);
        setError("");
      } else {
        setError(data.message || "Failed to process request");
        setMessage("");
      }
    } catch (error) {
      setError("Error: Could not connect to server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBonuses();
  }, []);

  const handleAddBonus = () => {
    if (!userId || !amount) {
      setError("Please enter user ID and amount");
      return;
    }
    const postData = { user_id: userId, added_amount: amount };
    fetchBonuses(postData);
    setUserId("");
    setAmount("");
  };

  if (loading && bonuses.length === 0) return <LogoLoader />;

  return (
    <div className="bg-primary-100 min-h-screen p-4 pb-24">
      <div className="max-w-xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-black text-white italic uppercase tracking-tighter flex items-center justify-center gap-3">
            <span className="p-2 bg-secondary rounded-xl text-primary transform rotate-12">
              <FaPlusCircle size={24} />
            </span>
            Add Bonus
          </h1>
          <p className="text-gray-400 text-sm font-medium">Distribute rewards to user accounts</p>
        </div>

        {/* Input Form Card */}
        <div className="bg-primary rounded-3xl p-6 shadow-2xl border border-white/5 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-32 h-32 bg-secondary opacity-5 rounded-full -mr-16 -mt-16 blur-2xl"></div>
          
          <div className="space-y-4 relative z-10">
            {/* User ID Input */}
            <div className="relative group">
              <FaIdBadge className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-secondary transition-colors" />
              <input
                type="number"
                placeholder="Target User ID"
                className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-secondary transition-all"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
              />
            </div>

            {/* Amount Input */}
            <div className="relative group">
              <FaMoneyBillWave className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-secondary transition-colors" />
              <input
                type="number"
                placeholder="Bonus Amount (₹)"
                className="w-full bg-white/5 border border-white/10 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-gray-600 focus:outline-none focus:border-secondary transition-all"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>

            {/* Feedback Messages */}
            {error && (
              <div className="flex items-center gap-2 text-red-500 bg-red-500/10 p-4 rounded-2xl border border-red-500/20 animate-shake">
                <FaExclamationTriangle className="flex-shrink-0" />
                <p className="text-xs font-black uppercase">{error}</p>
              </div>
            )}
            {message && (
              <div className="flex items-center gap-2 text-green-500 bg-green-500/10 p-4 rounded-2xl border border-green-500/20 animate-fade-in">
                <FaCheckCircle className="flex-shrink-0" />
                <p className="text-xs font-black uppercase">{message}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleAddBonus}
              className="w-full bg-secondary text-primary font-black py-5 rounded-2xl shadow-xl transform transition-all active:scale-95 hover:shadow-secondary/20 hover:brightness-110 flex items-center justify-center gap-2 text-lg italic uppercase tracking-tighter"
            >
              Add Bonus Balance
            </button>
          </div>
        </div>

        {/* Bonus History Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-2">
            <h2 className="text-white font-bold text-lg flex items-center gap-2">
              <span className="w-1 h-6 bg-secondary rounded-full"></span>
              Bonus History
            </h2>
            <FaHistory className="text-gray-500" />
          </div>

          <div className="space-y-3">
            {bonuses.length > 0 ? (
              bonuses.map((b) => (
                <div key={b.id} className="bg-primary p-4 rounded-2xl border border-white/5 shadow-lg flex items-center justify-between group hover:border-secondary/20 transition-all">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-secondary group-hover:bg-secondary group-hover:text-primary transition-colors">
                      <FaUser />
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm leading-none mb-1">{b.user_name || "Unknown User"}</p>
                      <p className="text-gray-500 text-[10px] uppercase font-black tracking-widest">ID: {b.user_id}</p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-secondary font-black text-lg italic tracking-tighter">₹{b.added_amount}</p>
                    <div className="flex items-center gap-1 justify-end text-gray-600 text-[9px] font-bold uppercase">
                      <FaCalendarAlt size={8} />
                      {b.created_at.split(" ")[0]}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-primary/50 rounded-2xl p-10 text-center border border-dashed border-white/10">
                <FaHistory className="mx-auto text-gray-700 mb-4" size={32} />
                <p className="text-gray-500 text-sm font-medium">No bonus history available yet.</p>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default AddBonus;
